import { trackError, trackUserEvent } from '@vend/utilities'
import { FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS } from '../../constants'
import { SaleManager } from '../../managers/sale-manager'
import { PaymentRequestDto } from '../../models/invoicing-payment-request'
import {
  selectSaleId,
  selectSaleLocalId,
} from '../../state/slices/currentSale/currentSaleSlice'
import { store } from '../../state/store'
import { getFeatureFromCache } from '../../util/feature-cache'
import { createPaymentRequest } from './create-invoicing-payment-request'
import { ensureSaleIsSaved } from './ensure-sale-is-saved'

type CreatePaymentRequestFromSaleScreenResponse = {
  success: boolean
  paymentRequestId?: string
  requestedAmount?: number
  error?: string
}

/**
 * Unit of work for creating a payment request from the sale screen.
 * This orchestrates the necessary steps:
 * NOTE: We are only allowing submission of sales that require fulfillment (delivery or pickup).
 * 1. Ensure the current sale is saved (if not already saved)
 * 2. Create the payment request
 *
 * @param saleManager {SaleManager} The sale manager instance (provides access to $ngRedux)
 * @param paymentRequestData {PaymentRequestDto} The payment request data to send
 * @return {Promise<CreatePaymentRequestFromSaleScreenResponse>} A promise resolved when the operation completes
 */
export const createPaymentRequestFromSaleScreen = async (
  saleManager: SaleManager,
  paymentRequestData: PaymentRequestDto
): Promise<CreatePaymentRequestFromSaleScreenResponse> => {
  const state = store.getState()
  const saleId = selectSaleId(state)
  const saleLocalId = selectSaleLocalId(state)
  const saleIdentifier = saleId || saleLocalId
  const customerRef = paymentRequestData.customer?.customerRef

  try {
    // Validate feature flag
    if (!getFeatureFromCache(FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS)) {
      return {
        success: false,
        error: 'Invoicing Payment Requests feature is disabled',
        requestedAmount: paymentRequestData.requestedAmount,
      }
    }

    // Validate there is sale local ID
    if (!saleLocalId) {
      return {
        success: false,
        error: 'No sale local ID available to create payment request',
        requestedAmount: paymentRequestData.requestedAmount,
      }
    }

    trackUserEvent('Web Register', 'Payment Request Flow Started', {
      saleId: saleIdentifier,
      customerRef,
    })

    // Ensure sale is saved before creating payment request
    const saveResult = await ensureSaleIsSaved(saleManager, saleId, saleLocalId)
    if (!saveResult.success) {
      return {
        success: false,
        error: `Failed to save sale: ${saveResult.error}`,
        requestedAmount: paymentRequestData.requestedAmount,
      }
    }

    // Use saleLocalId for documentRefs as it's the primary identifier in the system
    // and is always available (unlike saleId which is updated async after sale is set as complete)
    if (!paymentRequestData.documentRefs.includes(saleLocalId)) {
      paymentRequestData.documentRefs.push(saleLocalId)
    }

    // Update metadata with the saved sale ID
    if (paymentRequestData.metadata) {
      paymentRequestData.metadata.documentRef = saleLocalId
    }

    // Create the payment request
    const paymentRequestResult = await createPaymentRequest(paymentRequestData)

    if (!paymentRequestResult.success) {
      return {
        success: false,
        error: paymentRequestResult.error,
        paymentRequestId: paymentRequestResult.paymentRequestId,
        requestedAmount: paymentRequestData.requestedAmount,
      }
    }

    trackUserEvent('Web Register', 'Payment Request Created from Sale Screen', {
      saleId: saleIdentifier,
      paymentRequestId: paymentRequestResult.paymentRequestId,
      customerRef,
    })

    return {
      success: true,
      paymentRequestId: paymentRequestResult.paymentRequestId,
      requestedAmount: paymentRequestData.requestedAmount,
    }
  } catch (err: any) {
    const errorMessage =
      err?.message || 'Unknown error occurred in payment request flow'

    trackError('Payment Request Flow Error', errorMessage, {
      saleId: saleIdentifier,
      customerRef,
    })

    return {
      success: false,
      error: errorMessage,
      requestedAmount: paymentRequestData.requestedAmount,
    }
  }
}
